import { FileText, Image, DollarSign, Users } from 'lucide-react'
import { useContentTasks } from '../hooks/useContentTasks'
import type { ContentTask } from '../hooks/useContentTasks'
import { usePersonas } from '../hooks/usePersonas'
import { useCostSummary } from '../hooks/useCosts'
import { useVisualSummary } from '../hooks/useVisualTasks'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'

export function DashboardPage() {
  const navigate = useNavigate()
  const { data: tasks, isLoading: tasksLoading } = useContentTasks()
  const { data: personas, isLoading: personasLoading } = usePersonas()
  const { data: costSummary, isLoading: costsLoading } = useCostSummary()
  const { data: visualSummary, isLoading: visualSummaryLoading } = useVisualSummary()

  const taskList = Array.isArray(tasks) ? tasks : []
  const totalContent = taskList.filter((t: ContentTask) => t.status === 'completed').length
  const totalImages = visualSummary?.total_imagenes ?? 0
  const monthlySpend = costSummary?.total_cost || 0
  const activePersonas = personas?.length || 0
  const budgetLimit = costSummary?.budget_limit || 50

  const recentTasks = taskList.slice(0, 5)

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'badge badge-success'
      case 'running':
        return 'badge badge-info'
      case 'failed':
        return 'badge badge-error'
      case 'cancelled':
        return 'badge text-slate-500 bg-slate-100'
      default:
        return 'badge badge-warning'
    }
  }

  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      pending: 'Pendiente',
      running: 'En proceso',
      completed: 'Completado',
      failed: 'Fallido',
      cancelled: 'Cancelado',
    }
    return statusMap[status] || status
  }

  const isLoading = tasksLoading || personasLoading || costsLoading || visualSummaryLoading

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
        <p className="text-slate-500 mt-1">Bienvenido a STRTGY Content Engine</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-50 rounded-lg">
              <FileText className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Contenido Total</p>
              <p className="text-2xl font-bold text-slate-800">
                {isLoading ? '...' : totalContent}
              </p>
              <p className="text-xs text-slate-400">Completados</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-50 rounded-lg">
              <Image className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Imágenes Generadas</p>
              <p className="text-2xl font-bold text-slate-800">
                {isLoading ? '...' : totalImages}
              </p>
              <p className="text-xs text-slate-400">Este mes</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-50 rounded-lg">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Gasto Mensual</p>
              <p className="text-2xl font-bold text-slate-800">
                {isLoading ? '...' : `$${monthlySpend.toFixed(2)}`}
              </p>
              <p className="text-xs text-slate-400">Límite: ${budgetLimit}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-50 rounded-lg">
              <Users className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Personas Activas</p>
              <p className="text-2xl font-bold text-slate-800">
                {isLoading ? '...' : activePersonas}
              </p>
              <p className="text-xs text-slate-400">Configuradas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Acciones rápidas */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4 text-slate-800">Acciones Rápidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/content')}
            className="p-4 bg-slate-50 hover:bg-slate-100 rounded-lg text-left transition-colors border border-slate-200"
          >
            <FileText className="w-8 h-8 text-emerald-600 mb-2" />
            <h3 className="font-medium text-slate-800">Generar Contenido</h3>
            <p className="text-sm text-slate-500">Crear un nuevo blog post o contenido social</p>
          </button>
          <button
            onClick={() => navigate('/visual')}
            className="p-4 bg-slate-50 hover:bg-slate-100 rounded-lg text-left transition-colors border border-slate-200"
          >
            <Image className="w-8 h-8 text-emerald-600 mb-2" />
            <h3 className="font-medium text-slate-800">Crear Visual</h3>
            <p className="text-sm text-slate-500">Generar imágenes para redes sociales</p>
          </button>
          <button
            onClick={() => navigate('/personas')}
            className="p-4 bg-slate-50 hover:bg-slate-100 rounded-lg text-left transition-colors border border-slate-200"
          >
            <Users className="w-8 h-8 text-emerald-600 mb-2" />
            <h3 className="font-medium text-slate-800">Gestionar Personas</h3>
            <p className="text-sm text-slate-500">Configurar personas AI y sus voces</p>
          </button>
        </div>
      </div>

      {/* Actividad reciente */}
      <div className="card mt-6">
        <h2 className="text-lg font-semibold mb-4 text-slate-800">Actividad Reciente</h2>
        {isLoading ? (
          <div className="text-center py-8 text-slate-400">
            <p>Cargando...</p>
          </div>
        ) : recentTasks.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <p>Sin actividad reciente</p>
            <p className="text-sm mt-1">Genera tu primer contenido para ver la actividad aquí</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentTasks.map((task: ContentTask) => (
              <div
                key={task.id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                onClick={() => navigate('/content')}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-medium text-slate-800">{task.topic}</h3>
                    <span className={getStatusBadgeClass(task.status)}>
                      {getStatusText(task.status)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    {task.channel} • {task.content_type}
                    {task.created_at && (
                      <> • {formatDistanceToNow(new Date(task.created_at), { addSuffix: true, locale: es })}</>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
